import Link from 'next/link';
import styled from 'styled-components';

import { ArticleContentIterator } from 'editorial/components/ArticleContentIterator';
import { ArticleHead } from 'editorial/components/ArticleHead';
import {
  ARTICLE_BODY_DESKTOP_PADDING,
  ARTICLE_BODY_MOBILE_PADDING,
} from 'editorial/styles/articleStyles';
import { IArticle } from 'editorial/types';
import { variableFontRegular } from 'styles/theme/text';
import { usePutBackHeaderOnPageChange } from 'shared/hooks/usePutBackHeaderOnPageChange';
import { useToggleHeaderOnScrollEvent } from 'shared/hooks/useToggleHeaderOnScrollEvent';
import { t } from 'localization/utils/t';

const TP = 'editorial.components.Article';

export const Article = ({ article, nextArticle }: { article: IArticle; nextArticle: IArticle }) => {
  const { author, category, sections, subject } = article;
  useToggleHeaderOnScrollEvent();
  usePutBackHeaderOnPageChange();

  return (
    <Article.Wrapper>
      <ArticleHead article={article} />
      <Article.Body>
        <ArticleContentIterator
          sections={sections}
          category={category}
          meta={{ subject, author }}
        />
      </Article.Body>
      <Article.Footer>
        {nextArticle?.slug && (
          <Link href={`/editorial/${nextArticle?.slug}`} passHref>
            <Article.Link>{t(`${TP}.nextArticle`, 'Next Article')}</Article.Link>
          </Link>
        )}
      </Article.Footer>
    </Article.Wrapper>
  );
};

Article.Wrapper = styled.article``;

Article.Body = styled.div`
  padding: 0 ${ARTICLE_BODY_MOBILE_PADDING}px 1em;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    padding: 0 ${ARTICLE_BODY_DESKTOP_PADDING}px 1em;
  }
`;

Article.Footer = styled.div`
  position: relative;
  z-index: 1;
  border-bottom: 1px solid ${({ theme }) => theme.colors.darkGray};
  padding: 30px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

Article.Link = styled.a`
  width: 160px;
  max-width: 160px;
  cursor: pointer;
  user-select: none;
  ${variableFontRegular({ size: '14px', height: '16px', spacing: '0' })};
  text-transform: capitalize;
  border: 1px solid ${({ theme }) => theme.colors.black};
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.black};
  outline: none;
  height: 45px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  :hover {
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;
